import { segmentStatus } from '@openx/types/segments';
import { differenceInDays, isValid, parseISO } from 'date-fns';
import type { AudienceForm, FormSegment, Group, GroupLike, LookalikeFormSegment, SegmentGroupItem } from '../types';

// Keep in sync with `isSoonToBeExpired` (audiences/utils) and `isSoonToDate` (@openx/utils/lib/date), which drive the
// drawer tree, the audiences list and the deal/package audience alerts
const DAYS_UNTIL_EXPIRATION_WARNING = 10;

const isValidSegmentExpirationDate = (segment: FormSegment): boolean => {
  if (!segment.expiration_date) {
    return false;
  }

  const expiryDate = parseISO(segment.expiration_date);
  if (!isValid(expiryDate)) {
    return false;
  }

  return true;
};

// Expired by status (as the drawer tree and deal/package alerts check it) or by a past expiration date
export const isSegmentExpired = (segment: FormSegment): boolean => {
  if (segment.status === segmentStatus.EXPIRED) {
    return true;
  }

  return isValidSegmentExpirationDate(segment) && parseISO(segment.expiration_date) < new Date();
};

export const isSegmentSoonToExpire = (segment: FormSegment): boolean => {
  const isValidExpirationDate = isValidSegmentExpirationDate(segment);

  if (isValidExpirationDate) {
    const expiryDate = parseISO(segment.expiration_date);
    const daysUntilExpiration = differenceInDays(expiryDate, new Date());
    return daysUntilExpiration <= DAYS_UNTIL_EXPIRATION_WARNING;
  }

  return false;
};

type SegmentCheckResult = {
  hasExpired: boolean;
  hasSoonToExpire: boolean;
};

const checkGroupSegments = (group: Omit<Group, 'uuid'>): SegmentCheckResult => {
  // Same segment types as the Segments section shows; lookalike segments can be added later if we need to
  const segmentTypes: Array<keyof Group> = ['direct', 'predefined'];
  let hasExpired = false;
  let hasSoonToExpire = false;

  for (const segmentType of segmentTypes) {
    const segmentGroup = group[segmentType] as SegmentGroupItem<FormSegment> | SegmentGroupItem<LookalikeFormSegment>;
    if (segmentGroup?.segments) {
      for (const segment of segmentGroup.segments) {
        if (isSegmentExpired(segment)) {
          hasExpired = true;
        } else if (isSegmentSoonToExpire(segment)) {
          hasSoonToExpire = true;
        }

        if (hasExpired && hasSoonToExpire) {
          break;
        }
      }
    }

    if (hasExpired && hasSoonToExpire) {
      break;
    }
  }

  return { hasExpired, hasSoonToExpire };
};

const checkGroups = (groups: GroupLike[]): SegmentCheckResult => {
  let hasExpired = false;
  let hasSoonToExpire = false;

  for (const group of groups) {
    if ('groups' in group && group.groups) {
      const result = checkGroups(group.groups);
      hasExpired = hasExpired || result.hasExpired;
      hasSoonToExpire = hasSoonToExpire || result.hasSoonToExpire;
    } else {
      const result = checkGroupSegments(group);
      hasExpired = hasExpired || result.hasExpired;
      hasSoonToExpire = hasSoonToExpire || result.hasSoonToExpire;
    }

    if (hasExpired && hasSoonToExpire) {
      break;
    }
  }

  return { hasExpired, hasSoonToExpire };
};

/**
 * Checks if the normalized_definition contains any expired segments or segments soon to expire
 * @param normalizedDefinition - The normalized definition object with includes and excludes
 * @param now - Optional date to use as the current date (defaults to new Date())
 * @returns true if any segment is expired, or if there are no expired segments but some are soon to expire (within 10 days)
 */

export const hasExpiredSegments = (
  normalizedDefinition: AudienceForm['normalized_definition'] | undefined
): { hasExpired: boolean; hasSoonToExpire: boolean } => {
  if (!normalizedDefinition) {
    return { hasExpired: false, hasSoonToExpire: false };
  }

  const includesGroups = normalizedDefinition.includes?.groups || [];
  const excludesGroups = normalizedDefinition.excludes?.groups || [];

  const includesResult = checkGroups(includesGroups);
  const excludesResult = checkGroups(excludesGroups);

  const hasExpired = includesResult.hasExpired || excludesResult.hasExpired;
  const hasSoonToExpire = includesResult.hasSoonToExpire || excludesResult.hasSoonToExpire;

  return {
    hasExpired,
    hasSoonToExpire,
  };
};
